// Count-up for KPI values: the number rolls from 0 to its target on mount with an
// ease-out cubic, so the rail reads as "live" rather than pre-printed.
//
// Honesty guard: the animation only ever passes through values BELOW the target and
// lands exactly on it — a reader who glances mid-flight sees an undershoot, never an
// overshoot. Skipped entirely under prefers-reduced-motion.
import { useEffect, useRef, useState } from 'react';

export function useCountUp(target, { duration = 700 } = {}) {
  const [value, setValue] = useState(() => (animatable(target) ? 0 : target));
  const raf = useRef(null);

  useEffect(() => {
    if (!animatable(target)) { setValue(target); return undefined; }
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      setValue(target);
      return undefined;
    }
    const t0 = performance.now();
    const step = (t) => {
      const p = Math.min(1, (t - t0) / duration);
      const eased = 1 - (1 - p) ** 3;
      setValue(target * eased);
      if (p < 1) raf.current = requestAnimationFrame(step);
      else setValue(target); // land exactly, no float drift
    };
    raf.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf.current);
  }, [target, duration]);

  return value;
}

function animatable(v) {
  return typeof v === 'number' && Number.isFinite(v);
}
